import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";
import { FbCampaign } from "./FacebookAds";

/**
 * API Quản lý Tiền tố Chiến dịch.
 *  - getCampaignPrefixes:   GET    /api/v1/be/campaign-prefixes      (search, per_page, page)
 *  - createCampaignPrefix:  POST   /api/v1/be/campaign-prefix        {prefix, description}
 *  - updateCampaignPrefix:  PUT    /api/v1/be/campaign-prefix/{id}   {prefix, description}
 *  - deleteCampaignPrefix:  DELETE /api/v1/be/campaign-prefix/{id}
 *  - getCampaignsByPrefix:  GET    /api/v1/be/fb-campaigns/by-prefix (prefix_id | prefix, status, search, per_page)
 */

export interface CampaignPrefix {
    id: number;
    prefix: string;
    description: string | null;
    created_at?: string;
    updated_at?: string;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const jsonHeaders = () => ({ ...authHeaders(), "Content-Type": "application/json" });

export async function getCampaignPrefixes(
    params: { search?: string; per_page?: number; page?: number } = {}
): Promise<{ status: boolean; items: CampaignPrefix[]; total: number; msg?: string }> {
    try {
        const q = new URLSearchParams();
        if (params.search) q.append("search", params.search);
        if (params.per_page) q.append("per_page", String(params.per_page));
        if (params.page) q.append("page", String(params.page));
        const res = await fetch(`${API_URL}/api/v1/be/campaign-prefixes?${q.toString()}`, { headers: authHeaders() });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data || {};
        return { status: !!json.status, items: d.items || [], total: Number(d.total || 0), msg: json.msg };
    } catch (e: any) {
        return { status: false, items: [], total: 0, msg: e?.message };
    }
}

export async function createCampaignPrefix(
    body: { prefix: string; description?: string }
): Promise<{ status: boolean; data?: CampaignPrefix; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/campaign-prefix`, {
            method: "POST",
            headers: jsonHeaders(),
            body: JSON.stringify(body),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg || extractError(json) };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function updateCampaignPrefix(
    id: number,
    body: { prefix: string; description?: string }
): Promise<{ status: boolean; data?: CampaignPrefix; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/campaign-prefix/${id}`, {
            method: "PUT",
            headers: jsonHeaders(),
            body: JSON.stringify(body),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg || extractError(json) };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function deleteCampaignPrefix(
    id: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/campaign-prefix/${id}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, msg: json.msg || extractError(json) };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function getCampaignsByPrefix(
    params: { prefix_id?: number; prefix?: string; status?: string; search?: string; per_page?: number; page?: number }
): Promise<{ status: boolean; items: FbCampaign[]; total: number; prefix_info?: CampaignPrefix | null; msg?: string }> {
    try {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") q.append(k, String(v));
        });
        const res = await fetch(`${API_URL}/api/v1/be/fb-campaigns/by-prefix?${q.toString()}`, { headers: authHeaders() });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data || {};
        return {
            status: !!json.status,
            items: d.data || [],
            total: Number(d.total || 0),
            prefix_info: json.prefix_info || null,
            msg: json.message,
        };
    } catch (e: any) {
        return { status: false, items: [], total: 0, msg: e?.message };
    }
}

// Gom thông báo lỗi validate của Laravel (vd: prefix đã tồn tại)
function extractError(json: any): string | undefined {
    if (!json) return undefined;
    if (typeof json.error === "string") return json.error;
    if (json.error && typeof json.error === "object") {
        const first = Object.values(json.error)[0];
        return Array.isArray(first) ? String(first[0]) : String(first);
    }
    if (json.errors && typeof json.errors === "object") {
        const first = Object.values(json.errors)[0];
        return Array.isArray(first) ? String(first[0]) : String(first);
    }
    return json.message;
}
